import React, { useState } from 'react';
import { Button } from 'reactstrap';
import { toast } from 'react-toastify';
import useAwardingApi from '../hooks/useAwardingApi';
import UploadDealRecapModal from './UploadDealRecapModal';
import PreviewDocumentModal from './PreviewDocumentModal';
import ResponseSection from './ResponseSection';
import { formatDateToDDMMYYYY } from '../../../utils/dateformatter';
import { AWARDING_SENDER, AWARDING_STATUS } from '../utils/awardingHelpers';

const DEAL_RECAP_DOCUMENT_TYPE = 1;

const formatFileSize = size => {
  if (!size) return '0 KB';
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

const AwardingTab = ({
  awardingId,
  awarding,
  responses = [],
  isLoadingResponses = false,
  onChanged,
  onRespondCreated,
  mode = 'evaluator',
}) => {
  const isSupplier = mode === 'supplier';
  const { uploadDocument, deleteDocument, createResponse } = useAwardingApi();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResponding, setIsResponding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const documents = (awarding?.documents ?? []).filter(
    doc => doc.documentType === DEAL_RECAP_DOCUMENT_TYPE
  );
  const dealRecap = documents[0] ?? null;
  const status = awarding?.status ?? AWARDING_STATUS.open;
  const isSent = status !== AWARDING_STATUS.open;
  const isConfirmed = status === AWARDING_STATUS.confirmed;
  const canModifyDocument = !isConfirmed && !isSupplier;

  const toggleModal = () => {
    if (isSubmitting) return;
    setIsModalOpen(prev => !prev);
  };

  const togglePreview = () => setIsPreviewOpen(prev => !prev);

  const handleSubmit = async ({
    file,
    companyMitraId,
    picName,
    tradingEmail,
  }) => {
    setIsSubmitting(true);
    try {
      await uploadDocument(awardingId, file, {
        documentType: DEAL_RECAP_DOCUMENT_TYPE,
        companyMitraId,
        picName,
        tradingEmail,
      });
      toast.success('Deal recap has been sent to Registered Supplier');
      setIsModalOpen(false);
      await onChanged?.();
    } catch (error) {
      toast.error(error.message || 'Failed to send deal recap');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async () => {
    if (!dealRecap?.id) return;

    setIsDeleting(true);
    try {
      await deleteDocument(dealRecap.id);
      toast.success('Deal recap has been removed');
      await onChanged?.();
    } catch (error) {
      toast.error(error.message || 'Failed to remove deal recap');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRespond = async ({ message }) => {
    setIsResponding(true);
    try {
      await createResponse(awardingId, {
        message,
        senderType: isSupplier
          ? AWARDING_SENDER.companyMitra
          : AWARDING_SENDER.pertamina,
      });
      toast.success('Response has been submitted');
      await onRespondCreated?.();
      return true;
    } catch (error) {
      toast.error(error.message || 'Failed to submit response');
      return false;
    } finally {
      setIsResponding(false);
    }
  };

  const documentUpdatedAt =
    dealRecap?.createdDate && formatDateToDDMMYYYY(dealRecap.createdDate);

  const renderStartAwarding = () => (
    <div className='border rounded p-5'>
      <div className='text-center'>
        <h5 className='fw-bold mb-1'>Start Awarding</h5>
        <p className='text-muted fs-13 mb-4'>
          Upload the deal recap and send to Registered Supplier winner.
        </p>

        <button
          type='button'
          onClick={toggleModal}
          className='d-inline-flex align-items-center gap-3 border border-primary rounded p-3 bg-soft-primary text-start'
          style={{ minWidth: '320px' }}
        >
          <span className='avatar-sm d-flex align-items-center justify-content-center bg-white rounded'>
            <i className='ri-upload-2-line fs-20 text-primary'></i>
          </span>
          <span>
            <span className='d-block fw-medium text-dark'>Upload File</span>
            <small className='text-muted'>Browse from your device</small>
          </span>
        </button>
      </div>
    </div>
  );

  const renderSentAwarding = () => (
    <>
      <div className='row g-3'>
        <div className={isSupplier ? 'col-12' : 'col-12 col-lg-6'}>
          <div className='border rounded p-3 h-100 d-flex align-items-center gap-3'>
            <span className='avatar-sm d-flex align-items-center justify-content-center bg-light rounded'>
              <i className='ri-file-pdf-line fs-20 text-danger'></i>
            </span>
            <div className='flex-grow-1'>
              <span className='d-block fw-semibold text-dark'>Deal Recap</span>
              <small className='text-muted'>
                {documentUpdatedAt
                  ? `Updated: ${documentUpdatedAt}`
                  : dealRecap
                    ? formatFileSize(dealRecap.fileSize)
                    : 'No document'}
              </small>
            </div>
            <div className='d-flex align-items-center gap-2'>
              <Button
                color='light'
                className='border bg-white shadow-none'
                onClick={togglePreview}
                disabled={!dealRecap}
              >
                View
              </Button>
              {canModifyDocument && (
                <Button
                  color='light'
                  className='border bg-white shadow-none'
                  onClick={toggleModal}
                >
                  <i className='ri-upload-2-line align-bottom me-1'></i>
                  {dealRecap ? 'Change Document' : 'Upload'}
                </Button>
              )}
              {canModifyDocument && dealRecap && (
                <button
                  type='button'
                  className='btn btn-ghost-danger btn-sm btn-icon'
                  onClick={handleRemove}
                  disabled={isDeleting}
                  title='Remove file'
                >
                  <i className='ri-delete-bin-line'></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {!isSupplier && (
          <div className='col-12 col-lg-6'>
            <div className='border rounded p-3 h-100 d-flex align-items-center gap-3'>
              <span className='avatar-sm d-flex align-items-center justify-content-center bg-light rounded'>
                <i className='ri-building-line fs-20 text-danger'></i>
              </span>
              <div>
                <small className='text-muted d-block'>Send to</small>
                <span className='fw-medium text-dark'>
                  {awarding?.companyMitraName || '-'}
                </span>
                {awarding?.picName && (
                  <small className='text-muted d-block'>
                    PIC: {awarding.picName}
                    {awarding?.tradingEmail
                      ? ` • ${awarding.tradingEmail}`
                      : ''}
                  </small>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <ResponseSection
        responses={responses}
        isLoading={isLoadingResponses}
        canRespond={!isConfirmed}
        isSubmitting={isResponding}
        onSubmit={handleRespond}
      />
    </>
  );

  return (
    <div className='p-1'>
      <h5 className='fw-bold mb-1'>Awarding</h5>
      <p className='text-muted fs-13 mb-3'>
        Bisa jadi Kalimat tambahan untuk membantu section mengenai apa ini
      </p>

      {isSent ? renderSentAwarding() : renderStartAwarding()}

      {!isSupplier && (
        <UploadDealRecapModal
          isOpen={isModalOpen}
          toggle={toggleModal}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          defaultValues={{
            companyMitraId: awarding?.companyMitraId ?? '',
            picName: awarding?.picName ?? '',
            tradingEmail: awarding?.tradingEmail ?? '',
          }}
        />
      )}

      <PreviewDocumentModal
        isOpen={isPreviewOpen}
        toggle={togglePreview}
        document={dealRecap}
      />
    </div>
  );
};

export default AwardingTab;
